import { Link } from "react-router-dom";

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-12 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold text-text-primary mb-4 pb-2 border-b border-bg-secondary">
        {title}
      </h2>
      <div className="text-text-secondary text-sm leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function SubSection({ title, children }) {
  return (
    <div className="mt-4">
      <h3 className="text-base font-semibold text-text-primary mb-2">{title}</h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Kbd({ children }) {
  return (
    <kbd className="px-1.5 py-0.5 mx-0.5 rounded bg-bg-secondary border border-bg-secondary text-text-primary text-xs font-mono">
      {children}
    </kbd>
  );
}

function Mockup({ children, label }) {
  return (
    <div className="my-3 rounded-lg border border-bg-secondary bg-bg-main p-3">
      {label && (
        <p className="text-[10px] uppercase tracking-wider text-text-secondary mb-2">
          {label}
        </p>
      )}
      {children}
    </div>
  );
}

function Tip({ tone = "blue", title, children }) {
  const map = {
    blue:    "border-blue-500/40 bg-blue-500/5 text-blue-200",
    amber:   "border-amber-500/40 bg-amber-500/5 text-amber-200",
    rose:    "border-rose-500/40 bg-rose-500/5 text-rose-200",
    emerald: "border-emerald-500/40 bg-emerald-500/5 text-emerald-200",
  };
  return (
    <div className={`my-3 rounded-md border-l-4 ${map[tone]} p-3 text-xs`}>
      {title && <p className="font-semibold mb-1">{title}</p>}
      <div className="text-text-secondary">{children}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Visual mock-ups — these mirror the actual UI components in the app so the
   guide stays accurate without needing real screenshots. Update if the live
   UI changes significantly.
   ───────────────────────────────────────────────────────────────────────── */

function MockRunChipRow() {
  const chip = "h-9 flex items-center justify-center rounded font-medium text-xs";
  return (
    <div className="grid grid-cols-10 gap-1.5">
      <div className={`${chip} bg-red-600 text-white`}>Wk</div>
      <div className={`${chip} bg-zinc-700/40 text-zinc-200`}>0</div>
      <div className={`${chip} bg-blue-600/20 text-blue-200`}>1</div>
      <div className={`${chip} bg-blue-600/20 text-blue-200`}>2</div>
      <div className={`${chip} bg-blue-600/20 text-blue-200`}>3</div>
      <div className={`${chip} bg-emerald-600/20 text-emerald-200`}>4</div>
      <div className={`${chip} bg-teal-600/20 text-teal-200`}>5</div>
      <div className={`${chip} bg-purple-600/20 text-purple-200`}>6</div>
      <div className={`${chip} bg-bg-secondary text-text-secondary`}>⚡</div>
      <div className={`${chip} bg-bg-secondary text-text-secondary`}>⋮</div>
    </div>
  );
}

function MockExtrasRow() {
  const ext = "px-2.5 py-1 rounded bg-bg-secondary text-text-secondary text-xs border border-bg-secondary";
  return (
    <div className="flex flex-wrap gap-x-2 gap-y-1.5">
      <span className={ext}>DB</span>
      <span className={ext}>Wd</span>
      <span className={ext}>NB</span>
      <span className={ext}>B</span>
      <span className={ext}>LB</span>
      <span className={ext}>SR</span>
      <span className={ext}>QR</span>
      <span className={ext}>FHit</span>
      <span className={ext}>DRS</span>
    </div>
  );
}

function MockScoreRibbon() {
  const ball = "min-w-[18px] h-5 rounded-sm flex items-center justify-center font-mono text-[10px]";
  return (
    <div className="flex items-center gap-2 text-[11px] flex-wrap">
      <span className="text-[9px] uppercase tracking-wider text-text-secondary">Recent</span>
      <div className="flex gap-0.5">
        <span className={`${ball} bg-bg-secondary/50 text-text-secondary`}>•</span>
        <span className={`${ball} bg-bg-secondary text-text-primary`}>1</span>
        <span className={`${ball} bg-emerald-500 text-white`}>4</span>
        <span className={`${ball} bg-amber-500/30 text-amber-300`}>wd</span>
        <span className={`${ball} bg-red-600 text-white`}>wk</span>
        <span className={`${ball} bg-bg-secondary text-text-primary`}>1+2</span>
        <span className={`${ball} bg-purple-600 text-white`}>6</span>
      </div>
      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-bg-secondary/40 border border-bg-secondary">
        <span>👥</span><span className="font-mono">47/32</span><span className="text-text-secondary text-[10px]">P'ship</span>
      </span>
      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300">
        <span>🎯</span><span className="font-mono">Need 86 from 60</span><span className="text-[10px] opacity-80">RR 8.60</span>
      </span>
      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-bg-secondary/40 border border-bg-secondary text-text-secondary">
        <span>⚡</span><span className="text-[10px] uppercase tracking-wider">Middle</span>
      </span>
    </div>
  );
}

function MockWagonWheel() {
  return (
    <svg viewBox="0 0 100 100" className="w-32 h-32 mx-auto">
      <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" className="text-bg-secondary" strokeWidth="0.5" />
      <circle cx="50" cy="50" r="25" fill="none" stroke="currentColor" className="text-bg-secondary" strokeWidth="0.3" strokeDasharray="2,1" />
      <line x1="50" y1="5" x2="50" y2="95" stroke="currentColor" className="text-bg-secondary" strokeWidth="0.3" />
      <line x1="5" y1="50" x2="95" y2="50" stroke="currentColor" className="text-bg-secondary" strokeWidth="0.3" />
      <line x1="50" y1="50" x2="85" y2="20" stroke="currentColor" className="text-emerald-400" strokeWidth="0.8" />
      <circle cx="85" cy="20" r="2" fill="currentColor" className="text-emerald-400" />
      <line x1="50" y1="50" x2="15" y2="70" stroke="currentColor" className="text-blue-400" strokeWidth="0.6" />
      <circle cx="15" cy="70" r="1.5" fill="currentColor" className="text-blue-400" />
      <text x="50" y="52" textAnchor="middle" className="text-text-secondary" fontSize="3">●</text>
    </svg>
  );
}

function MockHeader() {
  const pill = "px-2 py-0.5 rounded border text-[10px]";
  return (
    <div className="flex items-center justify-between bg-bg-primary border border-bg-secondary rounded p-2 text-xs">
      <div className="flex items-center gap-2">
        <span className="text-text-primary font-semibold">SRH 142/4</span>
        <span className="text-text-secondary">(15.3)</span>
        <span className="text-text-secondary">vs MI · T20</span>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={`${pill} bg-emerald-500/15 text-emerald-300 border-emerald-500/30`}>✓ Synced</span>
        <span className={`${pill} bg-violet-500/15 text-violet-300 border-violet-500/30`}>💎 347 ZC</span>
        <span className={`${pill} bg-bg-secondary text-text-secondary border-bg-secondary`}>⌨ OFF</span>
        <span className={`${pill} bg-bg-secondary text-text-secondary border-bg-secondary`}>🎤</span>
      </div>
    </div>
  );
}

function MockActionBar() {
  return (
    <div className="flex items-center gap-2 text-xs flex-wrap">
      <button className="px-3 py-1.5 rounded bg-amber-500/20 text-amber-200 border border-amber-500/30">↶ UNDO (28s)</button>
      <button className="px-3 py-1.5 rounded bg-blue-500/20 text-blue-200 border border-blue-500/30">✎ EDIT BALL</button>
      <button className="px-3 py-1.5 rounded bg-violet-500/20 text-violet-200 border border-violet-500/30">✨ AI Tag · 15 ZC</button>
      <button className="px-3 py-1.5 rounded bg-bg-secondary text-text-secondary">⋯ MORE</button>
      <button className="ml-2 px-5 py-1.5 rounded bg-button-primary text-white font-bold">SAVE BALL</button>
    </div>
  );
}

export default function UserGuide() {
  return (
    <div className="min-h-screen bg-bg-main py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Link to="/dashboard" className="text-text-secondary hover:text-text-primary text-sm">
            ← Back to dashboard
          </Link>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-3xl">📘</span>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary">User guide</h1>
          </div>
          <p className="text-text-secondary text-sm mt-2">
            Complete reference for every feature, button, and screen in ZenCricketData. Bookmark this page —
            it's organised in the order you'll need things during a real match.
          </p>
        </div>

        <Tip tone="blue" title="Visuals in this guide">
          The diagrams below are <strong>live HTML mock-ups</strong> that match the actual app components. If
          the real UI looks slightly different, the chip names, colours, and layout are what to look for —
          not the exact pixels.
        </Tip>

        <nav className="mb-10 bg-bg-primary border border-bg-secondary rounded-xl p-4">
          <p className="text-xs uppercase tracking-wider text-text-secondary mb-2">Table of contents</p>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-y-1 text-sm list-decimal list-inside">
            <li><a href="#getting-started" className="text-emerald-400 hover:underline">Getting started — login &amp; dashboard</a></li>
            <li><a href="#building-blocks" className="text-emerald-400 hover:underline">Building blocks — teams, players, competitions</a></li>
            <li><a href="#match-prep" className="text-emerald-400 hover:underline">Match prep — toss &amp; playing XI</a></li>
            <li><a href="#scoring-console" className="text-emerald-400 hover:underline">The scoring console layout</a></li>
            <li><a href="#scoring-a-ball" className="text-emerald-400 hover:underline">Scoring a ball — the full flow</a></li>
            <li><a href="#run-chips" className="text-emerald-400 hover:underline">Run chips &amp; manual-runs entry</a></li>
            <li><a href="#extras" className="text-emerald-400 hover:underline">Extras row — every toggle explained</a></li>
            <li><a href="#wicket-modal" className="text-emerald-400 hover:underline">Recording a wicket</a></li>
            <li><a href="#spatial" className="text-emerald-400 hover:underline">Pitch map, height, wagon wheel</a></li>
            <li><a href="#score-ribbon" className="text-emerald-400 hover:underline">Score ribbon — recent / partnership / RRR</a></li>
            <li><a href="#commentary" className="text-emerald-400 hover:underline">Commentary feed &amp; over summaries</a></li>
            <li><a href="#voice" className="text-emerald-400 hover:underline">Voice scoring</a></li>
            <li><a href="#editing" className="text-emerald-400 hover:underline">Editing past balls</a></li>
            <li><a href="#undo" className="text-emerald-400 hover:underline">Undo &amp; bulk undo</a></li>
            <li><a href="#innings-transitions" className="text-emerald-400 hover:underline">Innings transitions &amp; follow-on</a></li>
            <li><a href="#dls" className="text-emerald-400 hover:underline">DLS — rain-affected matches</a></li>
            <li><a href="#tests" className="text-emerald-400 hover:underline">Test-specific features</a></li>
            <li><a href="#shortcuts" className="text-emerald-400 hover:underline">Keyboard shortcuts</a></li>
            <li><a href="#after-match" className="text-emerald-400 hover:underline">After the match — scorecard &amp; reports</a></li>
            <li><a href="#profiles" className="text-emerald-400 hover:underline">Public profiles &amp; marketplace</a></li>
            <li><a href="#wallet-plans" className="text-emerald-400 hover:underline">Wallet, plans &amp; usage</a></li>
            <li><a href="#troubleshooting" className="text-emerald-400 hover:underline">Troubleshooting</a></li>
          </ol>
        </nav>

        {/* ───── 1 ──────────────────────────────────────────────────────── */}
        <Section id="getting-started" title="1. Getting started">
          <SubSection title="1.1 Logging in">
            <ol className="list-decimal list-inside space-y-1">
              <li>Open <span className="text-text-primary">zencricketdata.com/login</span></li>
              <li>The identifier field accepts <strong className="text-text-primary">email, phone number, or username</strong> — whatever you signed up with</li>
              <li>Enter your password and click <strong className="text-text-primary">Login</strong></li>
              <li>Forgot it? Click <strong className="text-text-primary">"Forgot password?"</strong> and check your inbox</li>
            </ol>
            <Tip tone="amber" title="Phone-OTP signup users">
              If you signed up via phone OTP and don't remember your username, log in with your phone number —
              the system auto-derives a username like <code>player.+91XXXXX</code> internally, but you don't
              need to type that.
            </Tip>
          </SubSection>

          <SubSection title="1.2 The dashboard">
            <p>
              After login you land on the dashboard. It's organised into three areas:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-text-primary">Hero card</strong> — your active match (Resume button), today's activity, streak, wallet pill</li>
              <li><strong className="text-text-primary">Action tiles</strong> — the main navigation (Score &amp; manage matches, Marketplace, Wallet, AI tools, User guide, Privacy…)</li>
              <li><strong className="text-text-primary">Subscription strip</strong> — your current plan + manage button</li>
              <li><strong className="text-text-primary">Resources strip</strong> — help, usage, support links</li>
            </ul>
            <p>
              The Resume tile picks the match you most recently scored a ball on — not the most recently
              created — so you always land back where you left off.
            </p>
          </SubSection>
        </Section>

        {/* ───── 2 ──────────────────────────────────────────────────────── */}
        <Section id="building-blocks" title="2. Building blocks — teams, players, competitions, matches">
          <p>Before you can score a match, four things have to exist in the system:</p>
          <ol className="list-decimal list-inside space-y-1">
            <li><strong className="text-text-primary">Teams</strong> — at <code>/cricket/teams</code>. Create with name + short name + colours.</li>
            <li><strong className="text-text-primary">Players</strong> — at <code>/cricket/players</code>, or add inline from a team's squad page.</li>
            <li><strong className="text-text-primary">Competition or Series</strong> — at <code>/cricket/competitions</code> or <code>/cricket/series</code>. A match must belong to one.</li>
            <li><strong className="text-text-primary">Match</strong> — created from a competition / series detail page, pick teams + format + date.</li>
          </ol>
          <Tip tone="emerald" title="Marketplace shortcut">
            Save 2+ hours of player entry by buying a tournament squad pack from{" "}
            <Link to="/marketplace" className="text-emerald-400 hover:underline">/marketplace</Link>. IPL,
            BBL, county packs all pre-populated.
          </Tip>
        </Section>

        {/* ───── 3 ──────────────────────────────────────────────────────── */}
        <Section id="match-prep" title="3. Match prep — toss &amp; playing XI">
          <p>From a match detail page, the prep flow runs:</p>
          <ol className="list-decimal list-inside space-y-2">
            <li>
              <strong className="text-text-primary">Toss</strong> — pick the toss winner, choose to bat or
              bowl. Persists <code>Match.toss</code>.
            </li>
            <li>
              <strong className="text-text-primary">Playing XI</strong> — pick 11 from each team's squad. You
              can mark Captain + Wicketkeeper here. Use the "Add" button to bring in a substitute or new
              player on the spot.
            </li>
            <li>
              <strong className="text-text-primary">Confirm scoring</strong> — the system locks the XI and
              shows the <strong>Start ball-by-ball scoring</strong> button.
            </li>
          </ol>
          <Tip tone="amber" title="Squad mid-season">
            Need to add a new player to the team mid-tournament? Open the team's Squad modal, search → click to
            add. No need to redo the playing XI flow.
          </Tip>
        </Section>

        {/* ───── 4 ──────────────────────────────────────────────────────── */}
        <Section id="scoring-console" title="4. The scoring console layout">
          <p>
            The scoring console opens at <code>/ball/scoring/&lt;match-id&gt;</code>. It's organised into a
            sticky <strong>header</strong> at the top and three <strong>columns</strong> below it.
          </p>

          <Mockup label="Sticky header (visible always)">
            <MockHeader />
          </Mockup>

          <p>
            The header shows:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong className="text-text-primary">Live score</strong> — runs/wickets and current over</li>
            <li><strong className="text-text-primary">Sync pill</strong> — green ✓ Synced, amber ⏳ Queued, red ⚠ Error</li>
            <li><strong className="text-text-primary">Wallet pill</strong> — your current ZC balance</li>
            <li><strong className="text-text-primary">Keyboard toggle</strong> — ⌨ ON / ⌨ OFF (default OFF to avoid accidents)</li>
            <li><strong className="text-text-primary">Voice mic</strong> — push-to-talk for voice scoring</li>
            <li><strong className="text-text-primary">Integrity badge</strong> (superuser only) — flags if aggregate runs ≠ sum of ball runs</li>
          </ul>

          <SubSection title="Three columns below the header">
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-text-primary">Left pane</strong> — score ribbon, video player (optional),
                recent balls + partnership, the run chip row, extras row, batter / bowler stats tables, the big
                <em> Save Ball</em> button, Undo / Edit pills.
              </li>
              <li>
                <strong className="text-text-primary">Centre pane</strong> — Pitch map → Height impact → Wagon
                wheel tabs (auto-progress as you click), New / Replacement / WK player pickers, commentary feed.
              </li>
              <li>
                <strong className="text-text-primary">Right pane</strong> — Delivery type, Stroke, Foot movement,
                Connection, Keeper activity (revealed progressively), Voice cheat sheet, Performance Metrics
                accordion.
              </li>
            </ul>
          </SubSection>
        </Section>

        {/* ───── 5 ──────────────────────────────────────────────────────── */}
        <Section id="scoring-a-ball" title="5. Scoring a ball — the full flow">
          <p>Every delivery follows the same shape. Watch the bowler run in and:</p>
          <ol className="list-decimal list-inside space-y-2">
            <li>
              <strong className="text-text-primary">Tap a run chip</strong> in the left pane — 0 / 1 / 2 / 3 / 4 / 5 / 6, or use the manual-runs ⋮ button for 7+ run cases.
            </li>
            <li>
              <strong className="text-text-primary">Mark extras</strong> if needed (wide / no-ball / bye / leg-bye / dead ball / short run).
            </li>
            <li>
              <strong className="text-text-primary">Mark wicket</strong> if needed — click Wk → opens the Wicket modal.
            </li>
            <li>
              <strong className="text-text-primary">Spatial inputs</strong> in the centre pane — click pitch zone → height impact → wagon-wheel landing point.
            </li>
            <li>
              <strong className="text-text-primary">Optional analytics</strong> in the right pane — stroke, foot movement, delivery type, etc.
            </li>
            <li>
              <strong className="text-text-primary">SAVE BALL</strong> — the big button. 1-second delay (or instant on double-tap) so you can interrupt if you noticed a mistake.
            </li>
          </ol>

          <Mockup label="Action bar (bottom of left pane)">
            <MockActionBar />
          </Mockup>

          <p>
            After save, the system automatically:
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>Rotates strike on odd physical runs</li>
            <li>Rotates strike at over-end</li>
            <li>Chains free hit when the previous ball was a no-ball</li>
            <li>Clears single-ball toggles (wide / no-ball / bye / leg-bye)</li>
            <li>Updates the score ribbon, partnership, required rate</li>
          </ul>
        </Section>

        {/* ───── 6 ──────────────────────────────────────────────────────── */}
        <Section id="run-chips" title="6. Run chips &amp; manual-runs entry">
          <Mockup label="The run-chip row">
            <MockRunChipRow />
          </Mockup>
          <p>From left to right:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong className="text-text-primary text-red-400">Wk</strong> — opens the Wicket modal (does NOT set runs; you still pick a runs chip if there were runs on the wicket ball, e.g. a stumping off 1 run)</li>
            <li><strong className="text-text-primary">0</strong> — dot ball</li>
            <li><strong className="text-text-primary text-blue-300">1, 2, 3</strong> — running between wickets</li>
            <li><strong className="text-text-primary text-emerald-300">4</strong> — auto-sets <em>is_boundary = true</em></li>
            <li><strong className="text-text-primary text-teal-300">5</strong> — auto-sets <em>is_boundary = true</em> (assumes 4-boundary + 1 overthrow, the common case). Toggle the ⚡ chip off if it was actually 4 byes + 1.</li>
            <li><strong className="text-text-primary text-purple-300">6</strong> — auto-sets <em>is_boundary = true</em></li>
            <li><strong className="text-text-primary">⚡ Boundary</strong> — manual toggle. Doesn't change runs, only marks whether the runs came from a boundary (matters for fours/sixes counts on the scorecard).</li>
            <li>
              <strong className="text-text-primary">⋮ Manual runs</strong> — click to open a small popover where
              you can type any value 0–10. Use this for cases the chip row doesn't cover:
              <ul className="list-disc list-inside ml-4 mt-1">
                <li>3 ran + 4 overthrow-boundary = 7</li>
                <li>4 hit + 4 overthrow-boundary = 8</li>
                <li>6 + 4 overthrow-boundary = 10 (the realistic max)</li>
              </ul>
              Don't forget to toggle the ⚡ chip if a 4 or 6 boundary was involved.
            </li>
          </ul>

          <Tip tone="amber" title="Overthrow handling">
            Under the founder model (2026-05-30), the <strong>runs you enter is the TOTAL</strong> — it already
            includes any overthrow portion. The optional "overthrow" field in the fielding panel is just a tag
            saying "N of those came from a fielding error" for analytics. It does NOT add to the totals.
          </Tip>
        </Section>

        {/* ───── 7 ──────────────────────────────────────────────────────── */}
        <Section id="extras" title="7. Extras row — every toggle explained">
          <Mockup label="The extras row">
            <MockExtrasRow />
          </Mockup>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-bg-secondary rounded-lg overflow-hidden">
              <thead className="bg-bg-primary">
                <tr>
                  <th className="text-left p-2 text-text-primary w-20">Chip</th>
                  <th className="text-left p-2 text-text-primary">What it does</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-secondary text-text-secondary">
                <tr><td className="p-2 font-mono">DB</td><td className="p-2"><strong className="text-text-primary">Dead ball</strong> (Law 20). No runs / no extras count, but the delivery is recorded.</td></tr>
                <tr><td className="p-2 font-mono">Wd</td><td className="p-2"><strong className="text-text-primary">Wide</strong>. Auto-adds 1 penalty + any extra runs you mark on the chip row. Ball is not legal; over doesn't advance.</td></tr>
                <tr><td className="p-2 font-mono">NB</td><td className="p-2"><strong className="text-text-primary">No-ball</strong>. 1 penalty + off-bat runs go to the batter. The NEXT ball auto-becomes a free hit.</td></tr>
                <tr><td className="p-2 font-mono">B</td><td className="p-2"><strong className="text-text-primary">Bye</strong>. Runs scored without bat contact (over/around the keeper). Not credited to the batter or bowler.</td></tr>
                <tr><td className="p-2 font-mono">LB</td><td className="p-2"><strong className="text-text-primary">Leg-bye</strong>. Same as bye but off the body, not the bat. Same crediting rules.</td></tr>
                <tr><td className="p-2 font-mono">SR</td><td className="p-2"><strong className="text-text-primary">Short run</strong> (Law 18). One run disallowed. Comes off off-bat first, then byes, then leg-byes. Strike does NOT rotate on the disallowed run.</td></tr>
                <tr><td className="p-2 font-mono">QR</td><td className="p-2"><strong className="text-text-primary">Quick running</strong>. Informational tag for analytics. No scoring impact.</td></tr>
                <tr><td className="p-2 font-mono">FHit</td><td className="p-2"><strong className="text-text-primary">Free hit</strong>. Only RUN_OUT / MANKAD dismissals are legal on this ball — system enforces. Auto-set after a no-ball.</td></tr>
                <tr><td className="p-2 font-mono">DRS</td><td className="p-2"><strong className="text-text-primary">Decision Review System</strong> — marks this ball as DRS-appealed for the scorecard.</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        {/* ───── 8 ──────────────────────────────────────────────────────── */}
        <Section id="wicket-modal" title="8. Recording a wicket">
          <ol className="list-decimal list-inside space-y-1">
            <li>Click <strong className="text-text-primary text-red-400">Wk</strong> on the run-chip row → opens the Wicket modal</li>
            <li>Pick the <strong className="text-text-primary">wicket type</strong> from 12 options</li>
            <li>Pick the <strong className="text-text-primary">dismissed batter</strong> — defaults to striker (handles non-striker run-outs / Mankads correctly)</li>
            <li>Pick the <strong className="text-text-primary">dismissed by</strong> — defaults to bowler</li>
            <li>For CAUGHT, pick the fielder. For STUMPED, pick the keeper. For RUN_OUT / MANKAD, pick fielder1 + fielder2 if a relay.</li>
            <li>Confirm → close modal. The Wk chip is now active red.</li>
            <li>Click <strong className="text-text-primary">SAVE BALL</strong> to commit.</li>
          </ol>

          <SubSection title="Wicket type reference">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-bg-secondary rounded-lg overflow-hidden">
                <thead className="bg-bg-primary">
                  <tr>
                    <th className="text-left p-2 text-text-primary">Type</th>
                    <th className="text-left p-2 text-text-primary">Bowler credited?</th>
                    <th className="text-left p-2 text-text-primary">Counts toward team wickets?</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-bg-secondary text-text-secondary">
                  <tr><td className="p-2">BOWLED, LBW, CAUGHT, STUMPED, HIT_WICKET</td><td className="p-2">✓ Yes</td><td className="p-2">✓ Yes</td></tr>
                  <tr><td className="p-2">RUN_OUT, MANKAD, OBSTRUCTING, HIT_TWICE, TIMED_OUT</td><td className="p-2">✗ No</td><td className="p-2">✓ Yes</td></tr>
                  <tr><td className="p-2">RETIRED_OUT</td><td className="p-2">✗ No</td><td className="p-2">✓ Yes</td></tr>
                  <tr><td className="p-2">RETIRED_HURT, RETIRED_NOT_OUT</td><td className="p-2">✗ No</td><td className="p-2">✗ No (batter can return)</td></tr>
                </tbody>
              </table>
            </div>
          </SubSection>

          <Tip tone="amber" title="Did batters cross?">
            For a CAUGHT dismissal, you'll be asked whether the batters crossed before the catch was completed.
            "Yes" puts the non-striker on strike for the next ball; "No" keeps the new batter on strike.
          </Tip>
        </Section>

        {/* ───── 9 ──────────────────────────────────────────────────────── */}
        <Section id="spatial" title="9. Pitch map, height impact, wagon wheel">
          <p>
            The centre pane has three spatial tabs that <strong className="text-text-primary">auto-progress</strong> as
            you click in each. They're optional — skip any tab to leave it unmarked.
          </p>
          <ol className="list-decimal list-inside space-y-1">
            <li><strong className="text-text-primary">Pitch map</strong> — click where the ball pitched (top-down stump view, batsman's perspective)</li>
            <li><strong className="text-text-primary">Height impact</strong> — click where the ball would have struck the batter (or did, for LBW)</li>
            <li><strong className="text-text-primary">Wagon wheel</strong> — click where the ball went off the bat (top-down 360° view from the centre of the pitch)</li>
          </ol>

          <Mockup label="Wagon wheel — example with a 4 and a 2">
            <MockWagonWheel />
            <p className="text-text-secondary text-[10px] text-center mt-1">
              Centre = batter. Lines + dots = where the ball went. Inner ring = boundary saved by infield.
            </p>
          </Mockup>

          <p>
            For boundary clicks, you can optionally also pick the fielder (useful for boundary saves / dives /
            overthrows). The fielding panel slides in next to the wagon wheel.
          </p>
        </Section>

        {/* ───── 10 ─────────────────────────────────────────────────────── */}
        <Section id="score-ribbon" title="10. Score ribbon — Recent / Partnership / Required Rate / Phase">
          <p>The horizontal strip above the run chips. Shows at-a-glance state of the innings:</p>
          <Mockup label="Score ribbon">
            <MockScoreRibbon />
          </Mockup>
          <ul className="list-disc list-inside space-y-1">
            <li><strong className="text-text-primary">Recent</strong> — last 8 balls as colored squares. Dot, runs, extras (amber), wicket (red), overthrow shown as "<em>off-bat</em>+<em>overthrow</em>" e.g. "1+2".</li>
            <li><strong className="text-text-primary">Partnership</strong> — current pair's runs/balls (server-derived, accurate even after retired-hurt return).</li>
            <li><strong className="text-text-primary">Need from balls</strong> — appears only in the chasing innings. Uses DLS-revised target if applied.</li>
            <li><strong className="text-text-primary">Phase</strong> — Powerplay (1–6) / Middle (7–15) / Death (16–20) for T20; PP1 / PP2 / PP3 for ODI.</li>
          </ul>
        </Section>

        {/* ───── 11 ─────────────────────────────────────────────────────── */}
        <Section id="commentary" title="11. Commentary feed &amp; over summaries">
          <p>
            Below the wagon wheel in the centre pane, a scrolling commentary feed shows every saved ball's
            auto-commentary line. <strong className="text-text-primary">Click any line to load that ball into the form for inline editing.</strong>
          </p>
          <p>
            Between overs, the system interleaves a green "Over X: N runs · K wkts · F×4 · S×6" summary so you
            can spot momentum shifts without doing the math yourself.
          </p>
          <Tip tone="emerald" title="Click to edit">
            The commentary line is the FASTEST way to edit a past ball — faster than the Edit Ball modal for
            simple fixes. Click → form loads → change → UPDATE BALL.
          </Tip>
        </Section>

        {/* ───── 12 ─────────────────────────────────────────────────────── */}
        <Section id="voice" title="12. Voice scoring">
          <p>
            Hold the mic icon in the header (or press the configured push-to-talk key) and speak the ball:
            "<em>Single, square leg, defensive push</em>" or "<em>Six over long-on, slog</em>" or "<em>Caught at slip, edge</em>".
          </p>
          <p>The system:</p>
          <ol className="list-decimal list-inside space-y-1">
            <li>Records the audio while held</li>
            <li>Sends to Whisper STT for transcription</li>
            <li>Sends transcript to Claude for ball-data parsing</li>
            <li>Auto-fills the chip grid for you to verify + Save</li>
          </ol>
          <p>
            The voice cheat sheet in the right pane lists the vocabulary the model is best at. Noise gate
            toggle (right pane, top) — turn ON for stadium / loud environments.
          </p>
          <p>
            Voice + AI features have separate pricing — see the{" "}
            <Link to="/help/ai-guide" className="text-violet-300 hover:underline">AI guide</Link> for details.
          </p>
        </Section>

        {/* ───── 13 ─────────────────────────────────────────────────────── */}
        <Section id="editing" title="13. Editing past balls">
          <SubSection title="Path A — inline (fastest)">
            <p>
              Click the commentary line in the centre pane. The ball loads into the main form. Change what you
              need to. The big button changes from "SAVE BALL" to "UPDATE BALL" — click it.
            </p>
          </SubSection>
          <SubSection title="Path B — full editor (more powerful)">
            <p>
              Click the <strong className="text-text-primary text-blue-300">✎ EDIT BALL</strong> pill in the action bar.
              Opens a modal with 6 tabs:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong className="text-text-primary">Outcome</strong> — runs, boundary, free-hit, dead-ball</li>
              <li><strong className="text-text-primary">Extras</strong> — wide / no-ball / byes / leg-byes / penalty</li>
              <li><strong className="text-text-primary">Wicket</strong> — type, dismissed player, fielders</li>
              <li><strong className="text-text-primary">Players</strong> — striker / non-striker / bowler swap</li>
              <li><strong className="text-text-primary">Spatial</strong> — pitch map / height / wagon wheel (full graphical editor)</li>
              <li><strong className="text-text-primary">Commentary</strong> — text edit + ✨ Regenerate (3 ZC)</li>
            </ul>
          </SubSection>
          <SubSection title="Path C — voice cross-ball edit">
            <p>
              With voice scoring on, you can say "<em>Edit ball two point four to a wide</em>" — the system finds
              ball 2.4 in the recent balls list and opens the edit form pre-filled with the suggested change.
            </p>
          </SubSection>
        </Section>

        {/* ───── 14 ─────────────────────────────────────────────────────── */}
        <Section id="undo" title="14. Undo &amp; bulk undo">
          <p>
            After every save, an amber <strong className="text-text-primary">↶ UNDO (30s)</strong> pill appears in the
            action bar with a countdown.
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong className="text-text-primary">Within 30s:</strong> click → undoes the last ball cleanly. Aggregate, batter, bowler stats all roll back. The deleted ball is in the trash for 5 minutes for recovery.</li>
            <li><strong className="text-text-primary">After 30s:</strong> button greys out. Use <strong>EDIT BALL</strong> or delete via the editor.</li>
          </ul>
          <p>
            <strong className="text-text-primary">Bulk undo:</strong> in the editor's three-dot menu, "Undo last N balls"
            lets you wipe a whole over if a wrong bowler / wrong striker was tagged across the over.
          </p>
        </Section>

        {/* ───── 15 ─────────────────────────────────────────────────────── */}
        <Section id="innings-transitions" title="15. Innings transitions &amp; follow-on">
          <p>When an innings ends, the system shows a closure modal:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong className="text-text-primary">All out</strong> — 10 wickets fallen</li>
            <li><strong className="text-text-primary">Overs completed</strong> — max overs reached</li>
            <li><strong className="text-text-primary">Target reached</strong> — chasing side passed the target</li>
            <li><strong className="text-text-primary">Declared</strong> (Test only) — captain ends voluntarily</li>
            <li><strong className="text-text-primary">Forfeited</strong> / <strong className="text-text-primary">Abandoned</strong></li>
          </ul>
          <p>Pick → click Confirm → next innings opens (or match ends).</p>
          <SubSection title="Follow-on (Test cricket)">
            <p>
              After innings 2, if the team batting first leads by <strong className="text-text-primary">200+ runs</strong>,
              the system prompts: "Enforce follow-on?" Click Yes/No. Below 200 the prompt doesn't fire — the
              system proceeds to the normal innings-3 swap.
            </p>
          </SubSection>
          <SubSection title="Tied limited-overs matches">
            <p>
              If scores are level after both innings, you'll be asked "Start super over?" Boundary count
              decides the winner if the super over is also tied (ICC Law).
            </p>
          </SubSection>
        </Section>

        {/* ───── 16 ─────────────────────────────────────────────────────── */}
        <Section id="dls" title="16. DLS — applying Duckworth-Lewis-Stern">
          <ol className="list-decimal list-inside space-y-1">
            <li>In the scoring console, click <strong className="text-text-primary">MORE</strong> in the action bar</li>
            <li>Choose <strong className="text-text-primary">Apply DLS</strong></li>
            <li>Enter the revised target runs (exact "score to reach", NOT "runs to win")</li>
            <li>Enter the revised max overs</li>
            <li>Save</li>
          </ol>
          <p>
            The chasing innings aggregate updates with the new target. The required-rate chip in the score
            ribbon recalculates from the revised values.
          </p>
        </Section>

        {/* ───── 17 ─────────────────────────────────────────────────────── */}
        <Section id="tests" title="17. Test-specific features">
          <p>Test matches get extra controls in the header bar:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong className="text-text-primary">Day chip</strong> — Day 1 / 2 / 3 / 4 / 5</li>
            <li><strong className="text-text-primary">Session chip</strong> — Morning / Afternoon / Evening</li>
            <li><strong className="text-text-primary">Overs-today / remaining chips</strong> — auto-decrement, with a Pause toggle for rain/lunch breaks</li>
            <li><strong className="text-text-primary">Declare</strong> — in the closure modal, only visible in Tests</li>
          </ul>
          <p>
            Day/session changes are local to your browser until backend persistence is enabled — your scoring
            still works perfectly, just refresh = back to defaults.
          </p>
        </Section>

        {/* ───── 18 ─────────────────────────────────────────────────────── */}
        <Section id="shortcuts" title="18. Keyboard shortcuts">
          <p>
            Click the <strong className="text-text-primary">⌨ ON / ⌨ OFF</strong> pill in the header to toggle. Default
            is OFF so you don't score accidentally while typing in modals.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1 mt-2">
            <p><Kbd>0</Kbd>–<Kbd>6</Kbd> set runs</p>
            <p><Kbd>W</Kbd> toggle wide</p>
            <p><Kbd>N</Kbd> toggle no-ball</p>
            <p><Kbd>B</Kbd> toggle bye</p>
            <p><Kbd>L</Kbd> toggle leg-bye</p>
            <p><Kbd>K</Kbd> toggle wicket</p>
            <p><Kbd>U</Kbd> undo last ball</p>
            <p><Kbd>Enter</Kbd> / <Kbd>Space</Kbd> save ball</p>
            <p><Kbd>T</Kbd> cycle Pitch / Height / Wagon</p>
            <p><Kbd>Esc</Kbd> cancel / close modal</p>
            <p><Kbd>?</Kbd> shortcuts modal (always works)</p>
          </div>
        </Section>

        {/* ───── 19 ─────────────────────────────────────────────────────── */}
        <Section id="after-match" title="19. After the match — scorecard &amp; reports">
          <ol className="list-decimal list-inside space-y-1">
            <li>In the action bar → <strong className="text-text-primary">MORE</strong> → <strong className="text-text-primary">End match</strong></li>
            <li>Pick result type: DECISIVE / TIE / NO_RESULT / ABANDONED / DRAW (Tests only)</li>
            <li>System finalises <code>Match.result_type</code> and the public scorecard at <code>/cricket/matches/&lt;id&gt;/scorecard</code> becomes the canonical view</li>
          </ol>
          <p>From the Scorecard you can:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Copy the public live link to share with viewers</li>
            <li>Open Highlights — auto-curated boundary clips (if video was uploaded)</li>
            <li>Generate an AI Match Report (30 ZC) — narrative summary + MOM nomination</li>
            <li>Download a PDF scorecard</li>
          </ul>
        </Section>

        {/* ───── 20 ─────────────────────────────────────────────────────── */}
        <Section id="profiles" title="20. Public profiles &amp; marketplace">
          <SubSection title="Find-a-Scorer (your scorer-for-hire listing)">
            <p>
              <Link to="/account/portfolio" className="text-emerald-400 hover:underline">/account/portfolio</Link> — your
              public scorer listing. Elite plan required to receive booking requests. List your day rate,
              languages, formats covered, and availability.
            </p>
          </SubSection>
          <SubSection title="Find-a-Player (your cricketer profile)">
            <p>
              <Link to="/account/player-profile" className="text-emerald-400 hover:underline">/account/player-profile</Link> — your public
              cricketer profile. Scouts and coaches can find you via Find-a-Player. Includes your career stats
              auto-aggregated across every match you've appeared in, wagon-wheel patterns, pitch maps, video
              highlights.
            </p>
          </SubSection>
        </Section>

        {/* ───── 21 ─────────────────────────────────────────────────────── */}
        <Section id="wallet-plans" title="21. Wallet, plans &amp; usage">
          <SubSection title="Wallet">
            <p>
              At <Link to="/account/wallet" className="text-emerald-400 hover:underline">/account/wallet</Link>. Shows your
              ZC balance, transaction history, and topup button (Razorpay-powered).
            </p>
          </SubSection>
          <SubSection title="Plans &amp; subscription">
            <p>
              <Link to="/subscriptions" className="text-emerald-400 hover:underline">/subscriptions</Link> — pick / change
              plan. Plan tiers grant a monthly ZC bundle that resets on renewal. Pending plan changes show on
              the dashboard subscription strip.
            </p>
          </SubSection>
          <SubSection title="Usage (B2B / Org accounts)">
            <p>
              <Link to="/cricket/usage" className="text-emerald-400 hover:underline">/cricket/usage</Link> — API calls,
              storage, plan limits. Only visible on Org accounts.
            </p>
          </SubSection>
        </Section>

        {/* ───── 22 ─────────────────────────────────────────────────────── */}
        <Section id="troubleshooting" title="22. Troubleshooting — common situations &amp; fixes">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-bg-secondary rounded-lg overflow-hidden">
              <thead className="bg-bg-primary">
                <tr>
                  <th className="text-left p-2 text-text-primary">Situation</th>
                  <th className="text-left p-2 text-text-primary">Fix</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-secondary text-text-secondary">
                <tr><td className="p-2">Saved wrong runs</td><td className="p-2">UNDO within 30s, or EDIT BALL → Outcome tab</td></tr>
                <tr><td className="p-2">Wrong wicket type</td><td className="p-2">EDIT BALL → Wicket tab → change</td></tr>
                <tr><td className="p-2">Marked free hit by mistake</td><td className="p-2">Toggle FHit off; if saved, EDIT BALL → Extras → uncheck</td></tr>
                <tr><td className="p-2">Wrong striker / non-striker</td><td className="p-2">Re-pick from dropdown live; EDIT BALL → Players if saved</td></tr>
                <tr><td className="p-2">"Striker = non-striker" error</td><td className="p-2">SAVE button disables until you pick a different non-striker</td></tr>
                <tr><td className="p-2">Match froze on tie / follow-on / DLS</td><td className="p-2">Refresh page (Ctrl+R) — saved balls persist</td></tr>
                <tr><td className="p-2">"Network error / queued" message</td><td className="p-2">Ball is in local IndexedDB queue, will sync. Don't refresh until "Synced"</td></tr>
                <tr><td className="p-2">Wallet pill shows 0 ZC mid-match</td><td className="p-2">Scoring keeps working — only AI features need ZC. Top up at /account/wallet.</td></tr>
                <tr><td className="p-2">"Match locked by another scorer"</td><td className="p-2">Someone else is scoring this match. Ask them to release the lock from their action bar.</td></tr>
                <tr><td className="p-2">Voice not transcribing</td><td className="p-2">Check mic permission, then noise-gate toggle in right pane. Some browsers need HTTPS.</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        <div className="mt-10 bg-bg-primary border border-bg-secondary rounded-xl p-5 space-y-2">
          <p className="text-sm text-text-secondary">
            Looking for AI-specific features (AI Tag, Suggest, Scorer Chat)? See the{" "}
            <Link to="/help/ai-guide" className="text-violet-300 hover:underline">AI guide</Link>.
          </p>
          <p className="text-sm text-text-secondary">
            Still stuck? Email{" "}
            <a href="mailto:a.manivannan@sportingzenminds.com" className="text-emerald-400 hover:underline">
              admin
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
}
